import React, { useState } from "react";
import {
	Bell,
	Building2,
	CreditCard,
	Filter,
	Plug,
	SlidersHorizontal,
	UserPlus,
} from "lucide-react";
import AppNavTree, { NavTreeNode } from "./AppNavTree";

/**
 * Tree modelled on the organisation Settings sections, but with a few nested
 * children added to demonstrate the hierarchy, search and selection behaviour.
 * `skipTranslation` is used here so labels render literally in the showcase.
 */
const SETTINGS_TREE: NavTreeNode[] = [
	{
		id: "org-settings",
		label: "Organisation settings",
		icon: Building2,
		children: [
			{ id: "org-general", label: "General" },
			{ id: "org-branding", label: "Branding & logo" },
			{ id: "org-roles", label: "Roles & permissions" },
		],
	},
	{
		id: "services-competences",
		label: "Services & competences",
		icon: SlidersHorizontal,
		children: [
			{ id: "services-branches", label: "Branches" },
			{ id: "services-list", label: "Services" },
			{ id: "competences", label: "Competences" },
		],
	},
	{
		id: "list-filters",
		label: "List filters",
		icon: Filter,
	},
	{
		id: "economy",
		label: "Economy",
		icon: CreditCard,
		children: [
			{ id: "economy-contracts", label: "Contracts & pricing" },
			{ id: "economy-ob", label: "OB rules" },
			{ id: "economy-salary", label: "Salary codes" },
			{ id: "economy-invoicing", label: "Invoicing" },
		],
	},
	{
		id: "notifications",
		label: "Notifications",
		icon: Bell,
		children: [
			{ id: "notifications-email", label: "Email templates" },
			{ id: "notifications-push", label: "Push notifications" },
		],
	},
	{
		id: "recruitment",
		label: "Recruitment",
		icon: UserPlus,
		children: [
			{ id: "recruitment-templates", label: "Message templates" },
			{ id: "recruitment-contract", label: "Employment contract" },
		],
	},
	{
		id: "integrations",
		label: "Integrations",
		icon: Plug,
		children: [
			{ id: "integrations-fortnox", label: "Fortnox" },
			{ id: "integrations-hogia", label: "Hogia Lön" },
		],
	},
];

const AppNavTreeExample: React.FC = () => {
	const [selectedId, setSelectedId] = useState<string>("org-general");
	const [selectedBranchId, setSelectedBranchId] = useState<string>("list-filters");

	return (
		<div className="space-y-10 p-6">
			{/* Primary, settings-style example */}
			<div>
				<h3 className="text-lg font-semibold mb-4">Settings navigation (leaf selection)</h3>
				<div className="flex gap-6">
					<div className="w-72 rounded-lg border border-gray-200 bg-white p-3">
						<AppNavTree
							nodes={SETTINGS_TREE}
							selectedId={selectedId}
							onSelect={setSelectedId}
							searchPlaceholder="Search settings…"
							defaultExpandedIds={["org-settings"]}
							skipTranslation
							emptyLabel="No matching settings"
							treeClassName="max-h-[420px]"
						/>
					</div>
					<div className="flex-1 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6">
						<p className="text-sm text-gray-500">Selected section</p>
						<p className="mt-1 font-mono text-sm text-gray-900">{selectedId}</p>
						<p className="mt-4 text-xs text-gray-400">
							Type in the search box to filter — matching branches auto-expand and the match is
							highlighted.
						</p>
					</div>
				</div>
			</div>

			{/* Selectable branches variant */}
			<div>
				<h3 className="text-lg font-semibold mb-4">Selectable branches</h3>
				<p className="mb-3 text-sm text-gray-600">
					With <code className="text-xs">selectableBranches</code>, clicking a parent both expands
					it and selects it.
				</p>
				<div className="w-72 rounded-lg border border-gray-200 bg-white p-3">
					<AppNavTree
						nodes={SETTINGS_TREE}
						selectedId={selectedBranchId}
						onSelect={setSelectedBranchId}
						selectableBranches
						searchable={false}
						defaultExpandedIds={["economy"]}
						skipTranslation
					/>
				</div>
				<p className="mt-2 text-sm text-gray-600">
					Selected: <span className="font-mono">{selectedBranchId}</span>
				</p>
			</div>

			{/* No search, disabled node */}
			<div>
				<h3 className="text-lg font-semibold mb-4">Without search · disabled node</h3>
				<div className="w-72 rounded-lg border border-gray-200 bg-white p-3">
					<AppNavTree
						nodes={[
							{
								id: "available",
								label: "Available section",
								icon: Building2,
								children: [{ id: "available-child", label: "Sub section" }],
							},
							{ id: "disabled", label: "Disabled section", icon: Plug, disabled: true },
						]}
						searchable={false}
						defaultExpandedIds={["available"]}
						selectedId={selectedId}
						onSelect={setSelectedId}
						skipTranslation
					/>
				</div>
			</div>
		</div>
	);
};

export default AppNavTreeExample;
